import { useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { BulkClientStep } from '@/components/logsheets-maker/bulk/BulkClientStep';
import { BulkMachineStep } from '@/components/logsheets-maker/bulk/BulkMachineStep';
import {
  BulkScheduleStep,
  type BulkScheduleValues,
} from '@/components/logsheets-maker/bulk/BulkScheduleStep';
import { BulkWorkOrderStep } from '@/components/logsheets-maker/bulk/BulkWorkOrderStep';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useMakerActiveWorkOrdersQuery } from '@/hooks/useLogsheetMakerQueries';
import { useAuth } from '@/lib/auth/AuthContext';
import { bulkCreateLogsheetMakerRequest } from '@/services/logsheets-maker';
import type {
  BulkCreateBody,
  BulkCreateResult,
  BulkRowBody,
  MakerWorkOrderListItem,
} from '@/types/logsheets-maker';

/**
 * Phase 24 (v1.4) — CreateMakerLogsheetBulkWizardPage
 *
 * 4-step bulk-create wizard:
 *   Step 1 → BulkClientStep
 *   Step 2 → BulkWorkOrderStep
 *   Step 3 → BulkScheduleStep
 *   Step 4 → BulkMachineStep (inline submit; no separate Review step)
 *
 * BulkReviewStep was deliberately omitted for v1.4 — the most mobile-friendly
 * UX collapses Review into the Machine step's "Save N Drafts" CTA. The plan
 * brief explicitly authorised omission ("Phase 25 candidate" below).
 *
 * On Step 4's submit:
 *  - Builds the `BulkCreateBody`
 *  - Calls `bulkCreateLogsheetMakerRequest`
 *  - On 200: toast + navigate to `/logsheets/maker?status=Draft&fromDate&toDate`
 *  - On 400 BulkValidationError: re-thrown to BulkMachineStep so it can paint
 *    failing cards red.
 *
 * Sticky-CTA + step indicator dots live in BulkMachineStep + this page header
 * respectively; mobile uses md:hidden / md:flex switches.
 */

// Phase 25 candidate: BulkReviewStep — re-evaluate once UAT signals from v1.4.

const STEP_COUNT = 4;

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function nowHm(): string {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/** Convert HH:mm + ISO date to an ISO datetime (UTC). The backend accepts ISO
 *  datetimes; we keep zone-handling identical to the existing single-create
 *  wizard's startTime/endTime shape (`YYYY-MM-DDTHH:mm:00Z`). */
function toIsoDatetime(date: string, hhmm: string): string {
  return `${date}T${hhmm}:00Z`;
}

export function CreateMakerLogsheetBulkWizardPage(): JSX.Element {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [clientName, setClientName] = useState<string | null>(null);
  const [workOrder, setWorkOrder] = useState<MakerWorkOrderListItem | null>(null);
  const [schedule, setSchedule] = useState<BulkScheduleValues | null>(null);

  const wosQuery = useMakerActiveWorkOrdersQuery();

  if (!permissions.includes('logsheet.maker.create')) {
    return <Navigate to="/dashboard" replace />;
  }

  function goToStep(target: 1 | 2 | 3 | 4): void {
    // Don't allow forward jumps past unset gates.
    if (target === 2 && clientName === null) return;
    if (target === 3 && workOrder === null) return;
    if (target === 4 && schedule === null) return;
    setStep(target);
  }

  async function submitBulk(
    rows: readonly BulkRowBody[],
  ): Promise<BulkCreateResult> {
    if (workOrder === null || schedule === null) {
      // Defensive — the step's render-gate (`schedule !== null`) means this
      // branch is effectively unreachable, but the BulkMachineStep contract
      // requires us to return a BulkCreateResult either way.
      throw new Error('Bulk wizard reached submitBulk without WO + schedule.');
    }
    const body: BulkCreateBody = {
      workOrderId: workOrder.id,
      entryDate: schedule.entryDate,
      shift: schedule.shift,
      startTime: toIsoDatetime(schedule.entryDate, schedule.startTime),
      endTime:
        schedule.endTime === null
          ? null
          : toIsoDatetime(schedule.entryDate, schedule.endTime),
      rows: rows.map((r) => ({
        machineId: r.machineId,
        type: r.type,
        workDescription: r.workDescription,
        idleReason: r.idleReason,
        notes: r.notes,
        kpiOverrides: r.kpiOverrides,
      })),
    };
    return await bulkCreateLogsheetMakerRequest(body);
  }

  function onBulkComplete(_result: BulkCreateResult): void {
    if (schedule === null) return;
    void queryClient.invalidateQueries({ queryKey: ['logsheets-maker'] });
    void navigate(
      `/logsheets/maker?status=Draft&fromDate=${schedule.entryDate}&toDate=${schedule.entryDate}`,
    );
  }

  const wos = wosQuery.data ?? [];
  const siteIdForDup =
    workOrder !== null && workOrder.sites.length > 0
      ? (workOrder.sites[0]?.siteId ?? null)
      : null;

  return (
    <div className="space-y-6" data-testid="bulk-wizard-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
          Bulk create logsheet entries
        </h1>
        <p className="text-muted-foreground text-sm">
          Step {String(step)} of {String(STEP_COUNT)} — pick client, work order, schedule, then mark
          each machine.
        </p>
      </div>

      {/* Step indicator dots: tappable to go back to earlier completed steps. */}
      <div className="flex items-center gap-2">
        {([1, 2, 3, 4] as const).map((n) => {
          const isActive = step === n;
          const isPast = step > n;
          const canGo =
            n === 1 ||
            (n === 2 && clientName !== null) ||
            (n === 3 && workOrder !== null) ||
            (n === 4 && schedule !== null);
          return (
            <button
              key={n}
              type="button"
              onClick={() => {
                if (canGo) goToStep(n);
              }}
              disabled={!canGo}
              data-testid={`bulk-step-indicator-${String(n)}`}
              aria-label={`Go to step ${String(n)}`}
              aria-current={isActive ? 'step' : undefined}
              className={
                'h-2 flex-1 rounded-full transition-colors ' +
                (isActive
                  ? 'bg-primary'
                  : isPast
                    ? 'bg-primary/60'
                    : 'bg-muted')
              }
              style={{ minHeight: '12px' }}
            />
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {step === 1 && 'Pick a client'}
            {step === 2 && 'Pick a work order'}
            {step === 3 && 'Schedule'}
            {step === 4 && 'Mark machines'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {step === 1 && (
            <BulkClientStep
              onSelectClient={(name) => {
                setClientName(name);
                // Reset downstream picks when client changes.
                setWorkOrder(null);
                setSchedule(null);
                setStep(2);
              }}
              selectedClientName={clientName}
            />
          )}
          {step === 2 && clientName !== null && (
            <BulkWorkOrderStep
              workOrders={wos}
              clientName={clientName}
              onSelectWorkOrder={(wo) => {
                setWorkOrder(wo);
                setSchedule(null);
                setStep(3);
              }}
              selectedWorkOrderId={workOrder?.id ?? null}
              onBack={() => {
                setStep(1);
              }}
            />
          )}
          {step === 3 && workOrder !== null && (
            <BulkScheduleStep
              initial={
                schedule ?? {
                  entryDate: todayIso(),
                  shift: null,
                  startTime: nowHm(),
                  endTime: null,
                }
              }
              onSubmit={(values) => {
                // Step 3 owns entryDate + shift only — merge with prior
                // schedule so timing (set in step 4) survives a Back→Next
                // round-trip.
                setSchedule((prev) => ({
                  entryDate: values.entryDate,
                  shift: values.shift,
                  startTime: prev?.startTime ?? nowHm(),
                  endTime: prev?.endTime ?? null,
                }));
                setStep(4);
              }}
              onBack={() => {
                setStep(2);
              }}
            />
          )}
          {step === 4 && workOrder !== null && schedule !== null && (
            <BulkMachineStep
              workOrder={workOrder}
              schedule={schedule}
              siteIdForDuplicateCheck={siteIdForDup}
              onSubmit={submitBulk}
              onComplete={onBulkComplete}
              onBack={() => {
                setStep(3);
              }}
              onScheduleChange={(next) => {
                setSchedule(next);
              }}
            />
          )}

          {/* Defensive fallback: if a step is reached without prerequisites,
              offer a way back. Should be unreachable via normal flow. */}
          {step === 2 && clientName === null && (
            <div className="space-y-2 text-sm">
              <p>No client picked yet.</p>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setStep(1);
                }}
              >
                Pick a client
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
